import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Case, CasesQueryParams } from '../app/models/case';
import { environment } from '../environments/environment';
import { PaginatedCollection } from 'src/app/models/PaginatedCollection';

@Injectable({
  providedIn: 'root'
})
export class CasesService {
  private readonly casesUrl = `${environment.api.baseUrl}${environment.api.endpoints.cases}`;

  constructor(private http: HttpClient) {}

  /**
   * An admin is tenant-less unless it says otherwise, which is what makes every call here span all
   * tenants. Passing a tenantId adds X-T-ID, and the backend then scopes to the cases where that
   * tenant is the doctor or the tech.
   */
  private buildHeaders(tenantId?: number | string): HttpHeaders {
    if (tenantId !== undefined && tenantId !== null && String(tenantId).trim() !== '') {
      return new HttpHeaders({ 'X-T-ID': String(tenantId) });
    }
    return new HttpHeaders();
  }

  /**
   * Get all cases with optional filtering and pagination.
   */
  getAllCases(params: CasesQueryParams): Observable<PaginatedCollection<Case>> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.set('page', params.page.toString());
    }

    if (params.size !== undefined) {
      queryParams.set('size', params.size.toString());
    }

    if (params.sort && params.sort.length > 0) {
      params.sort.forEach(sortParam => {
        queryParams.append('sort', sortParam);
      });
    }

    if (params.doctorId !== undefined) {
      queryParams.set('doctorId', params.doctorId.toString());
    }

    if (params.techId !== undefined) {
      queryParams.set('techId', params.techId.toString());
    }

    if (params.searchTerm !== undefined && params.searchTerm.trim() !== '') {
      queryParams.set('searchTerm', params.searchTerm.trim());
    }

    // Repeated rather than comma-joined, same as sort: the backend binds status to a List<CaseStatus>
    // and ORs the values.
    if (params.statuses && params.statuses.length > 0) {
      params.statuses.forEach(status => {
        queryParams.append('status', status);
      });
    }

    if (params.createdAtFrom !== undefined && params.createdAtFrom.trim() !== '') {
      queryParams.set('createdAtFrom', params.createdAtFrom.trim());
    }

    if (params.createdAtTo !== undefined && params.createdAtTo.trim() !== '') {
      queryParams.set('createdAtTo', params.createdAtTo.trim());
    }

    // Only sent when true — the backend's default is "exclude archived", and archivedOnly wins.
    if (params.includeArchived) {
      queryParams.set('includeArchived', 'true');
    }

    if (params.archivedOnly) {
      queryParams.set('archivedOnly', 'true');
    }

    const url = `${this.casesUrl}?${queryParams.toString()}`;
    return this.http.get<PaginatedCollection<Case>>(url, { headers: this.buildHeaders(params.tenantId) });
  }

  /**
   * Get a specific case by ID
   */
  getCaseById(id: string): Observable<Case> {
    return this.http.get<Case>(`${this.casesUrl}/${id}`, { headers: this.buildHeaders() });
  }

  /**
   * Delete a case by ID. The tenant is optional — an admin may delete across tenants — but passing
   * the case's own tenant keeps the call working against backends without the admin bypass.
   */
  deleteCase(id: string, tenantId?: number | string): Observable<void> {
    return this.http.delete<void>(`${this.casesUrl}/${id}`, { headers: this.buildHeaders(tenantId) });
  }
}
